import { twAuthClient } from "@teamworksdev/web-sdk/auth";
import { Box } from "@mui/material";
import {
  AskTeamworksBottomBar,
  AskTeamworksProvider,
  ChatLayout,
  FloatingChatSurface,
  SidebarChatSurface,
  useAskTeamworksContext,
} from "@teamworksdev/ai-react";
import "@teamworksdev/ai-react/styles.css";
import { type RefObject, useCallback, useLayoutEffect, useRef, useState } from "react";
import { environment } from "../../environment";
import { useTeamworksAuthContext } from "../../contexts/TeamworksAuth";
import { useUser } from "../../utils/tokens";

// Per-environment via VITE_ASK_TEAMWORKS_API_URL (apps/api runs unproxied on :3300 in dev).
const ASK_TEAMWORKS_API_URL = environment.VITE_ASK_TEAMWORKS_API_URL;

// Scopes the widget to only the nutrition product agent (and, transitively, only
// the mcp app's tools) -- see apps/api's PRODUCT_AGENT_REGISTRY in ask-teamworks-app.
const NUTRITION_AGENT_ID = "nutrition";

// The org layout's <main> (OrgRouter) is the whole right-hand side of the app, next to the left nav.
const MAIN_CONTENT_ID = "main-content";

interface MainContentLayout {
  main: { top: number; left: number; width: number; height: number };
  barHeight: number;
}

// The dashboard renders inside <main>'s padding and a scrolling content box, neither of which the bar or fullscreen
// chat can escape from within the flow. So both are fixed-positioned over <main>'s measured rect (which tracks the
// nav width) instead of hardcoding its geometry.
const useMainContentLayout = (barRef: RefObject<HTMLElement>) => {
  const [layout, setLayout] = useState<MainContentLayout | null>(null);

  useLayoutEffect(() => {
    const bar = barRef.current;
    const main = document.getElementById(MAIN_CONTENT_ID);
    if (!bar || !main) {
      return undefined;
    }

    const measure = () => {
      const { top, left, width, height } = main.getBoundingClientRect();
      const barHeight = bar.getBoundingClientRect().height;
      setLayout(prev =>
        prev &&
        prev.barHeight === barHeight &&
        prev.main.top === top &&
        prev.main.left === left &&
        prev.main.width === width &&
        prev.main.height === height
          ? prev
          : { main: { top, left, width, height }, barHeight }
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(main);
    observer.observe(bar);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [barRef]);

  return layout;
};

// The shell persists chatMode, so every mode the view switcher offers must render a surface or the chat vanishes.
const AskTeamworksSurfaces = ({
  fullscreenBounds,
}: {
  fullscreenBounds: { top: number; left: number; width: number; height: number } | undefined;
}) => {
  const { chatMode, isOpen } = useAskTeamworksContext();

  if (!isOpen) {
    return null;
  }

  switch (chatMode) {
    case "floating":
      return <FloatingChatSurface />;
    case "sidebar":
      return <SidebarChatSurface />;
    case "fullscreen":
      if (!fullscreenBounds) {
        return null;
      }
      return (
        <Box
          sx={{
            position: "fixed",
            ...fullscreenBounds,
            zIndex: 1,
            overflow: "hidden",
            borderTopLeftRadius: 12,
            backgroundColor: ({ palette: { mode } }) => (mode === "light" ? "white" : "black"),
          }}
        >
          <ChatLayout />
        </Box>
      );
    default:
      return null;
  }
};

const AskTeamworksShell = () => {
  const barRef = useRef<HTMLDivElement>(null);
  const layout = useMainContentLayout(barRef);

  return (
    <>
      <AskTeamworksSurfaces
        fullscreenBounds={layout ? { ...layout.main, height: Math.max(layout.main.height - layout.barHeight, 0) } : undefined}
      />
      {/* Keeps the dashboard's space reserved now that the bar itself is fixed-positioned. */}
      {layout && <div style={{ height: layout.barHeight }} />}
      <div
        ref={barRef}
        style={
          layout
            ? {
                position: "fixed",
                left: layout.main.left,
                width: layout.main.width,
                top: layout.main.top + layout.main.height - layout.barHeight,
                zIndex: 1,
              }
            : undefined
        }
      >
        <AskTeamworksBottomBar />
      </div>
    </>
  );
};

/**
 * Ask Teamworks trigger + chat surfaces (floating, sidebar, fullscreen) for the Menu Order Analytics dashboard.
 *
 * Renders nothing until the Unity auth client (shared with the AMS/messaging MFE
 * integrations) has a token -- orgs that aren't axle-synced never get one, so the
 * widget stays hidden for them rather than showing a trigger that can never authenticate.
 */
export const AskTeamworksWidget = () => {
  const { isAuthReady } = useTeamworksAuthContext();
  // The Unity token's `sub` is per org membership, and Ask Teamworks only lets a token's owner modify its
  // conversations. Scoping by membership starts a fresh conversation on org switch instead of a 403.
  const authScope = useUser()?.orgMembership?.axlePersonDocumentId ?? undefined;

  const getToken = useCallback(async () => (await twAuthClient.ensureToken()) ?? "", []);

  if (!isAuthReady) {
    return null;
  }

  return (
    <AskTeamworksProvider
      apiUrl={ASK_TEAMWORKS_API_URL}
      getToken={getToken}
      authScope={authScope}
      lockedAgentId={NUTRITION_AGENT_ID}
      greeting={{ title: "Ask about menu orders", subtitle: "Ask about menu item performance, ingredient usage, and order trends." }}
      placeholder="Ask about menu orders..."
    >
      <AskTeamworksShell />
    </AskTeamworksProvider>
  );
};
